<template>
  <div class="pa-4">
    <div class="d-flex align-center mb-4">
      <h2 class="text-h6">
        清算（レジ締）
      </h2>
      <v-spacer />
      <v-btn
        color="primary"
        :to="{ name: 'admin-cash-register-closing-new', params: { club_slug: $route.params.club_slug } }"
      >
        新規清算
      </v-btn>
    </div>

    <v-data-table
      :headers="headers"
      :items="closings"
      :loading="loading"
      :items-per-page="20"
      class="elevation-1"
      @click:row="onRowClick"
    >
      <template #[`item.businessDate`]="{ item }">
        {{ item.businessDate }}
      </template>
      <template #[`item.status`]="{ item }">
        <v-chip
          small
          :color="item.status === 'finalized' ? 'success' : 'warning'"
        >
          {{ item.status === 'finalized' ? '確定済' : '下書き' }}
        </v-chip>
      </template>
      <template #[`item.countedCashAmount`]="{ item }">
        {{ formatYen(item.countedCashAmount) }}
      </template>
      <template #[`item.expectedCashAmount`]="{ item }">
        {{ formatYen(item.expectedCashAmount) }}
      </template>
      <template #[`item.diffAmount`]="{ item }">
        <span :class="diffClass(item.diffAmount)">{{ formatYen(item.diffAmount) }}</span>
      </template>
      <template #[`item.finalizedAt`]="{ item }">
        {{ item.finalizedAt ? formatDateTime(item.finalizedAt) : '-' }}
      </template>
      <template #[`item.actions`]="{ item }">
        <v-btn
          v-if="item.status === 'draft'"
          icon
          small
          color="error"
          title="削除"
          @click.stop="confirmDestroy(item)"
        >
          <v-icon small>
            {{ mdiDelete }}
          </v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <v-dialog
      v-model="destroyDialog"
      max-width="420"
    >
      <v-card>
        <v-card-title>下書きの削除</v-card-title>
        <v-card-text>
          営業日 {{ destroyTarget && destroyTarget.businessDate }} の下書きを削除しますか？<br>
          関連する金種カウント・原因記録も一緒に削除されます。
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="destroyDialog = false"
          >
            キャンセル
          </v-btn>
          <v-btn
            color="error"
            :loading="destroying"
            @click="executeDestroy"
          >
            削除する
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { ref, onMounted, getCurrentInstance } from '@vue/composition-api'
import { mdiDelete } from '@mdi/js'
import CashRegisterClosingApi from '@/api/admin/CashRegisterClosing'

export default {
  name: 'AdminCashRegisterClosings',
  setup() {
    const vm = getCurrentInstance().proxy
    const closings = ref([])
    const loading = ref(false)

    const headers = [
      { text: '営業日', value: 'businessDate' },
      { text: 'ステータス', value: 'status' },
      { text: '金種カウント', value: 'countedCashAmount', align: 'end' },
      { text: '帳簿額', value: 'expectedCashAmount', align: 'end' },
      { text: '差額', value: 'diffAmount', align: 'end' },
      { text: '確定日時', value: 'finalizedAt' },
      {
        text: '操作', value: 'actions', sortable: false, align: 'end',
      },
    ]

    const fetch = async () => {
      loading.value = true
      try {
        const res = await CashRegisterClosingApi.getClosings()
        const data = res.data?.cashRegisterClosings?.data || []
        closings.value = data.map(d => ({ id: d.id, ...d.attributes }))
      } finally {
        loading.value = false
      }
    }

    const onRowClick = item => {
      const target = item.status === 'finalized'
        ? 'admin-cash-register-closing-detail'
        : 'admin-cash-register-closing-new'

      vm.$router.push({
        name: target,
        params: { club_slug: vm.$route.params.club_slug, id: item.id },
        query: item.status === 'finalized' ? {} : { id: item.id },
      })
    }

    const formatYen = v => `¥${Number(v || 0).toLocaleString()}`
    const formatDateTime = v => new Date(v).toLocaleString('ja-JP')
    const diffClass = v => {
      if (v === 0) return ''

      return v > 0 ? 'success--text' : 'error--text'
    }

    const destroyDialog = ref(false)
    const destroyTarget = ref(null)
    const destroying = ref(false)

    const confirmDestroy = item => {
      destroyTarget.value = item
      destroyDialog.value = true
    }

    const executeDestroy = async () => {
      if (!destroyTarget.value) return
      destroying.value = true
      try {
        await CashRegisterClosingApi.destroyClosing(destroyTarget.value.id)
        destroyDialog.value = false
        destroyTarget.value = null
        await fetch()
      } finally {
        destroying.value = false
      }
    }

    onMounted(fetch)

    return {
      closings,
      loading,
      headers,
      onRowClick,
      formatYen,
      formatDateTime,
      diffClass,
      mdiDelete,
      confirmDestroy,
      destroyDialog,
      destroyTarget,
      destroying,
      executeDestroy,
    }
  },
}
</script>
